import React, { useState } from "react";
import { couponsData } from "../data/coupons"; // 請確保路徑正確
import {
  Tag,
  X,
  ZoomIn,
  CheckCircle2,
  ExternalLink,
  Ticket,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function CouponPage() {
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  // 處理點擊邏輯：如果有網址就開網頁，沒有就開圖片Modal
  const handleCouponClick = (coupon) => {
    if (coupon.url) {
      window.open(coupon.url, "_blank", "noopener,noreferrer");
    } else {
      setSelectedCoupon(coupon);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      {/* 頁面標題 */}
      <div className="bg-white p-6 rounded-3xl shadow-warm border border-gold/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-starbucks/5 rounded-full blur-2xl pointer-events-none"></div>
        <h2 className="text-2xl font-bold mb-1 flex items-center gap-2 text-starbucks-dark font-serif">
          <Tag className="text-gold" /> 優惠券收納
        </h2>
        <p className="text-xs text-hojicha font-bold tracking-wider uppercase ml-1">
          Coupons & Deals
        </p>
      </div>

      {/* --- 新增區塊：外部優惠券集合連結 (LikeJapan) --- */}
      <a
        href="https://www.likejapan.com/%e6%97%85%e9%81%8a%e5%84%aa%e6%83%a0%e5%88%b8-likejapan%e5%84%aa%e6%83%a0?utm_source=google&utm_medium=paidsearch&utm_campaign=travelcoupon_twhk_nov2025&gad_source=1&gad_campaignid=23274359188&gbraid=0AAAABCA0Qh_bOr0N2T0-UhEIRyBKCtFUL&gclid=CjwKCAiAybfLBhAjEiwAI0mBBvDnGzShF8VskWdnw-XWzulwVfx_ovmAJL38sEkrcCVNu8hNb9Aj_xoCgQ8QAvD_BwE"
        target="_blank"
        rel="noopener noreferrer"
        className="group flex w-full items-center justify-between rounded-2xl border border-stone-200 bg-white/80 p-4 shadow-sm backdrop-blur-sm transition-all hover:bg-white active:scale-98"
      >
        <div className="flex items-center gap-3">
          {/* 圖示 */}
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-orange-500 group-hover:bg-orange-100 transition-colors">
            <Ticket size={24} />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-text-primary text-lg font-serif">
              LikeJapan 優惠集合
            </span>
            <span className="text-xs text-gray-500">
              點擊查看多家藥妝、電器綜合優惠
            </span>
          </div>
        </div>
        <ExternalLink
          size={20}
          className="text-gray-300 group-hover:text-orange-400 transition-colors"
        />
      </a>
      {/* ----------------------------------------------- */}

      {/* 優惠券列表 */}
      <div className="grid gap-6">
        {couponsData.map((coupon) => (
          <motion.div
            key={coupon.id}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleCouponClick(coupon)} // 改用新的處理函數
            className="bg-white rounded-3xl shadow-warm border border-hojicha-light/20 overflow-hidden cursor-pointer group relative"
          >
            {/* 上半部：品牌色條與標題 */}
            <div className="p-5 relative">
              {/* 左側裝飾條 */}
              <div
                className="absolute left-0 top-0 bottom-0 w-2"
                style={{ backgroundColor: coupon.color }}
              ></div>

              <div className="flex justify-between items-start pl-3">
                <div className="flex-1">
                  <h3 className="font-bold text-xl text-text-primary mb-1 font-serif">
                    {coupon.name}
                  </h3>
                  <span className="inline-block bg-red-50 text-red-600 text-xs font-bold px-2 py-1 rounded-md border border-red-100 mb-2">
                    {coupon.desc}
                  </span>
                  <p className="text-xs text-hojicha mb-4 leading-relaxed">
                    {coupon.note}
                  </p>
                </div>
                {/* 按鈕圖示：根據是否為網頁版顯示不同圖示 */}
                <div className="ml-2 w-10 h-10 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-starbucks group-hover:text-white transition-colors">
                  {coupon.url ? (
                    <ExternalLink size={18} />
                  ) : (
                    <ZoomIn size={18} />
                  )}
                </div>
              </div>

              {/* 下半部：折扣級距 */}
              {coupon.tiers && (
                <div className="mt-2 bg-warm-paper/50 rounded-xl p-3 border border-hojicha/10">
                  <p className="text-[10px] font-bold text-hojicha uppercase tracking-widest mb-2 ml-1">
                    折扣門檻
                  </p>
                  <ul className="space-y-1.5">
                    {coupon.tiers.map((tier, idx) => (
                      <li
                        key={idx}
                        className="text-xs text-text-secondary flex items-start gap-2"
                      >
                        <CheckCircle2
                          size={12}
                          className="text-starbucks mt-0.5 shrink-0"
                        />
                        <span>{tier}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* 網頁版提示標籤 (選用) */}
            {coupon.url && (
              <div className="absolute top-0 right-0 bg-stone-100 text-[10px] text-stone-500 px-2 py-1 rounded-bl-lg">
                Web Coupon
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* 彈出視窗 (Modal) - 只針對沒有 URL 的圖片型優惠券 */}
      <AnimatePresence>
        {selectedCoupon && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex flex-col justify-center items-center p-4"
            onClick={() => setSelectedCoupon(null)}
          >
            <button
              className="absolute top-6 right-6 p-3 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors"
              onClick={() => setSelectedCoupon(null)}
            >
              <X size={28} />
            </button>

            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white p-2 rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={selectedCoupon.image}
                  alt={selectedCoupon.name}
                  className="w-full h-auto object-contain brightness-110"
                />
              </div>
              <div className="p-4 text-center">
                <h3 className="font-bold text-xl text-gray-800">
                  {selectedCoupon.name}
                </h3>
                <p className="text-sm text-gray-500 mt-1 font-mono">
                  請向店員出示此畫面
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
